import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Chip,
  Stack,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

function FishLogGallery() {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [catches, setCatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchCatches = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const docRef = doc(db, 'user-catches', userId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Extract catches from the document
          // Each root property represents a catch with nested imageUrl
          const catchesArray = [];
          Object.keys(data).forEach(catchId => {
            const catchData = data[catchId];
            if (catchData && typeof catchData === 'object' && catchData.imageUrl) {
              catchesArray.push({
                id: catchId,
                imageUrl: catchData.imageUrl,
                ...catchData
              });
            }
          });
          
          // Sort catches by most recent first
          catchesArray.sort((a, b) => {
            const getTimestamp = (catchData) => {
              // Try dateTime first, then timestamp, then createdAt
              const timestamp = catchData.dateTime || catchData.timestamp || catchData.createdAt;
              if (!timestamp) return 0;
              
              try {
                // Handle Firestore timestamp or regular timestamp
                return timestamp.toDate ? timestamp.toDate().getTime() : new Date(timestamp).getTime();
              } catch (err) {
                return 0;
              }
            };
            
            return getTimestamp(b) - getTimestamp(a); // Descending order (most recent first)
          });
          
          setCatches(catchesArray);
        } else {
          setCatches([]);
        }
      } catch (err) {
        console.error('Error fetching catches:', err);
        setError('Failed to load fish log data');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchCatches();
    }
  }, [userId]);

  const handleImageClick = (catchData) => {
    setSelectedImage(catchData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
  };

  const handleBackClick = () => {
    navigate('/users');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      // Handle Firestore timestamp or regular timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mb: 2, width: 'auto' }}
        >
          Back to Users
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          variant="outlined"
          sx={{ mb: 2, width: 'auto' }}
        >
          Back to Users
        </Button>
        <Typography variant="h4" component="h1">
          Fish Log Gallery
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Total Catches: {catches.length}
        </Typography>
      </Paper>

      {catches.length === 0 ? (
        <Alert severity="info">
          No catches found for this user.
        </Alert>
      ) : (
        <ImageList variant="masonry" cols={3} gap={8}>
          {catches.map((catchData) => (
            <ImageListItem key={catchData.id}>
              <img
                src={catchData.imageUrl}
                alt={`Catch ${catchData.id}`}
                loading="lazy"
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'transform 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                }}
                onClick={() => handleImageClick(catchData)}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <ImageListItemBar
                subtitle={formatDate(catchData.dateTime)}
                title={catchData.length + "\""}
                actionIcon={
                  <IconButton
                    sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                    onClick={() => handleImageClick(catchData)}
                  >
                    <ZoomInIcon />
                  </IconButton>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Image Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
          </Typography>
          <IconButton onClick={handleCloseDialog} sx={{mb:2, width: 'auto'}}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <img
                  src={selectedImage.imageUrl}
                  alt={`Catch ${selectedImage.id}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '500px',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              </Box>
              
              <Stack spacing={2}>
                {/* Basic Information */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Catch ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedImage.catchId || selectedImage.id}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date & Time
                  </Typography>
                  <Typography variant="body1">
                    {selectedImage.dateTime ? new Date(selectedImage.dateTime).toLocaleString() : formatDate(selectedImage.timestamp || selectedImage.createdAt)}
                  </Typography>
                </Box>

                {/* Fish Details */}
                {(selectedImage.length || selectedImage.weight || selectedImage.girth) && (
                  <Box>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Fish Details
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {selectedImage.length && (
                        <Chip label={`Length: ${selectedImage.length}"`} variant="outlined" />
                      )}
                      {selectedImage.weight && (
                        <Chip label={`Weight: ${selectedImage.weight} lbs`} variant="outlined" />
                      )}
                      {selectedImage.girth && (
                        <Chip label={`Girth: ${selectedImage.girth}"`} variant="outlined" />
                      )}
                    </Stack>
                  </Box>
                )}

                {selectedImage.caughtBy && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Caught By
                    </Typography>
                    <Typography variant="body1">
                      {selectedImage.caughtBy}
                    </Typography>
                  </Box>
                )}

                {/* Location Details */}
                {(selectedImage.lake || selectedImage.latitude || selectedImage.longitude) && (
                  <Box>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Location
                    </Typography>
                    {selectedImage.lake && (
                      <Typography variant="body1">
                        <strong>Lake:</strong> {selectedImage.lake}
                      </Typography>
                    )}
                    {selectedImage.latitude && selectedImage.longitude && (
                      <Typography variant="body2" color="text.secondary">
                        Coordinates: {selectedImage.latitude}, {selectedImage.longitude}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Lure Information */}
                {(selectedImage.lureName || selectedImage.lureColor || selectedImage.lureType) && (
                  <Box>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Lure Details
                    </Typography>
                    {selectedImage.lureName && (
                      <Typography variant="body1">
                        <strong>Name:</strong> {selectedImage.lureName}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {selectedImage.lureType && (
                        <Chip label={selectedImage.lureType} size="small" />
                      )}
                      {selectedImage.lureColor && (
                        <Chip label={selectedImage.lureColor} size="small" variant="outlined" />
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Technique */}
                {(selectedImage.presentation || selectedImage.speed) && (
                  <Box>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Technique
                    </Typography>
                    {selectedImage.presentation && (
                      <Typography variant="body1">
                        <strong>Presentation:</strong> {selectedImage.presentation}
                      </Typography>
                    )}
                    {selectedImage.speed && (
                      <Typography variant="body1">
                        <strong>Speed:</strong> {selectedImage.speed}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Environmental Conditions */}
                {(selectedImage.depth || selectedImage.bottomType || selectedImage.structure) && (
                  <Box>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Water Conditions
                    </Typography>
                    {selectedImage.depth && (
                      <Typography variant="body1">
                        <strong>Depth:</strong> {selectedImage.depth}
                      </Typography>
                    )}
                    {selectedImage.bottomType && (
                      <Typography variant="body1">
                        <strong>Bottom Type:</strong> {selectedImage.bottomType}
                      </Typography>
                    )}
                    {selectedImage.structure && (
                      <Typography variant="body1">
                        <strong>Structure:</strong> {selectedImage.structure}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Weather Conditions */}
                {(selectedImage.pressure || selectedImage.windDirection || selectedImage.windSpeed || selectedImage.skyConditions) && (
                  <Box>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Weather Conditions
                    </Typography>
                    <Stack spacing={1}>
                      {selectedImage.pressure && (
                        <Typography variant="body1">
                          <strong>Pressure:</strong> {selectedImage.pressure}" {selectedImage.pressureTrend && `(${selectedImage.pressureTrend})`}
                        </Typography>
                      )}
                      {(selectedImage.windDirection || selectedImage.windSpeed) && (
                        <Typography variant="body1">
                          <strong>Wind:</strong> {selectedImage.windDirection} {selectedImage.windSpeed && `at ${selectedImage.windSpeed} mph`}
                        </Typography>
                      )}
                      {selectedImage.skyConditions && (
                        <Typography variant="body1">
                          <strong>Sky:</strong> {selectedImage.skyConditions}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Moon Conditions */}
                {(selectedImage.moonPhase || selectedImage.moonEvent || selectedImage.percentVisible) && (
                  <Box>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Moon Conditions
                    </Typography>
                    <Stack spacing={1}>
                      {selectedImage.moonPhase && (
                        <Typography variant="body1">
                          <strong>Phase:</strong> {selectedImage.moonPhase}
                        </Typography>
                      )}
                      {selectedImage.percentVisible && (
                        <Typography variant="body1">
                          <strong>Visibility:</strong> {selectedImage.percentVisible}%
                        </Typography>
                      )}
                      {selectedImage.moonEvent && (
                        <Typography variant="body1">
                          <strong>Event:</strong> {selectedImage.moonEvent}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Timing */}
                {(selectedImage.minutes || selectedImage.beforeAfter) && (
                  <Box>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Timing
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {selectedImage.minutes && (
                        <Chip label={`${selectedImage.minutes} minutes`} size="small" />
                      )}
                      {selectedImage.beforeAfter && (
                        <Chip label={selectedImage.beforeAfter} size="small" variant="outlined" />
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Legacy fields for backward compatibility */}
                {selectedImage.species && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Species
                    </Typography>
                    <Chip label={selectedImage.species} variant="outlined" />
                  </Box>
                )}

                {selectedImage.location && !selectedImage.lake && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1">
                      {selectedImage.location}
                    </Typography>
                  </Box>
                )}

                {selectedImage.notes && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedImage.notes}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FishLogGallery;
